import React, {
  ChangeEvent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react"

import {
  DateFilter,
  KeywordFilter,
  RangeFilter,
  SelectFilter,
  SearchableSelectFilter,
} from "./filters"
import { Divider } from "~/ui-rtk/components/ui/common"
import { Button, Select } from "~/ui-rtk/components/ui/controls"
import { type TMultiSelectOption } from "~/ui-rtk/components/ui/controls/MultiSelect"

import {
  isDateFilter,
  isFilteringAllowed,
  isRangeFilter,
  isSelectFilter,
  isTextFilter,
} from "./utils"

import type {
  TTextOperator,
  TableVisualizationColumnFilter,
} from "~/ui-rtk/constants/table-visualization-types"
import { TSelectOption } from "~/ui-rtk/components/ui/controls/Select"
import { TManualFilter } from "../../charts/TableVisualization/constants"
import { TFilterColumnConfig } from "./types"
import { cn } from "~/ui-rtk/utils/tailwind-utils"

type MenuProps = {
  onApply: (filter: TableVisualizationColumnFilter) => void
  onReset: () => void
  columnConfig: TFilterColumnConfig[]
  filterToEdit?: {
    idx: number
    filter: TableVisualizationColumnFilter
  }
  additionalFilters?: TManualFilter[]
  align?: "right" | "left"
  getSelectOptions?: (columnKey: string) => TMultiSelectOption[]
  isLoadingOptions?: boolean
}

const Menu: React.FC<MenuProps> = ({
  columnConfig,
  onApply,
  onReset,
  filterToEdit,
  additionalFilters = [],
  align = "right",
  getSelectOptions,
  isLoadingOptions,
}) => {
  const [tableFilter, setTableFilter] =
    useState<TableVisualizationColumnFilter | null>(
      filterToEdit?.filter ?? null,
    )
  const [selectedColumn, setSelectedColumn] = useState<string | null>(null)

  const options = useMemo(
    () =>
      columnConfig.reduce(
        (acc: TSelectOption[], config) => {
          if (isFilteringAllowed(config.type) && !config.disableFiltering) {
            acc.push({
              value: config.key,
              label: config.header ?? config.key,
            })
          }

          return acc
        },
        [
          {
            value: "",
            label: "Select",
            disabled: true,
          } as TSelectOption,
          ...additionalFilters,
        ],
      ),
    [columnConfig],
  )

  const setInitialState = () => {
    setTableFilter(null)
    setSelectedColumn(null)
  }

  const handleResetFilters = () => {
    setInitialState()
    onReset()
  }

  const handleSelect = (event: ChangeEvent<HTMLSelectElement>) => {
    setSelectedColumn(event?.target?.value)
  }

  const handleApplyFilters = () => {
    if (!tableFilter) {
      return
    }

    onApply({
      ...tableFilter,
      idx: filterToEdit?.idx,
    })
    setSelectedColumn(null)
  }

  const changeState =
    (variant: "text" | "range" | "date" | "select") =>
    (
      key: string,
      value:
        | string
        | string[]
        | { min?: number; max?: number }
        | { startDate?: Date | null; endDate?: Date | null },
      operator?: TTextOperator,
    ) => {
      const arrayedValue = Array.isArray(value) ? value : [value]
      const filterValue =
        variant === "range" || variant === "date" ? value : arrayedValue

      // Find the column config to check if it has a filterKey
      const column = columnConfig.find(col => col.key === key)
      const filterId = column?.filterKey || key

      const filter: TableVisualizationColumnFilter = {
        id: filterId, // Use filterKey if provided, otherwise use key
        variant,
        value: filterValue,
        operator,
      }

      setTableFilter(filter)
    }

  useEffect(() => {
    if (filterToEdit) {
      // When editing, find the column that matches either key or filterKey
      const filterId = filterToEdit.filter.id
      let matchingColumn = columnConfig.find(col => col.key === filterId)

      // If not found by key, try filterKey
      if (!matchingColumn) {
        matchingColumn = columnConfig.find(col => col.filterKey === filterId)
      }

      // Set the selected column to the column's key (not filterKey)
      setSelectedColumn(matchingColumn?.key || filterId)
      setTableFilter(filterToEdit?.filter)
    }
  }, [filterToEdit, columnConfig])

  const renderFilters = useCallback(
    (columnKey: string) => {
      // When editing, we need to find the column by either key or filterKey
      let column = columnConfig.find(f => f.key === columnKey)

      // If not found by key, try to find by filterKey (for editing existing filters)
      if (!column && filterToEdit) {
        column = columnConfig.find(f => f.filterKey === columnKey)
      }
      if (!column) {
        const additionalFilter = additionalFilters.find(
          f => f.value === columnKey,
        )
        if (!additionalFilter) {
          return
        }

        // Use the correct filter type from additionalFilter
        const filterType = additionalFilter.type || "text"

        if (filterType === "range") {
          return (
            <RangeFilter
              tableFilter={filterToEdit?.filter}
              onChange={changeState("range")}
              column={
                {
                  key: columnKey,
                } as unknown as TFilterColumnConfig
              }
            />
          )
        } else if (filterType === "date") {
          return (
            <DateFilter
              tableFilter={filterToEdit?.filter}
              onChange={changeState("date")}
              column={
                {
                  key: columnKey,
                } as unknown as TFilterColumnConfig
              }
            />
          )
        } else if (filterType === "select") {
          return (
            <SelectFilter
              tableFilter={filterToEdit?.filter}
              onChange={changeState("select")}
              column={
                {
                  key: columnKey,
                } as unknown as TFilterColumnConfig
              }
              getOptions={getSelectOptions}
            />
          )
        } else if (filterType === "searchable-select") {
          return (
            <SearchableSelectFilter
              defaultFilter={filterToEdit?.filter}
              onChange={changeState("text")}
              column={
                {
                  key: columnKey,
                  header: additionalFilter.label,
                } as unknown as TFilterColumnConfig
              }
              getOptions={getSelectOptions}
              isLoadingOptions={isLoadingOptions}
            />
          )
        }
        // Default to text/keyword filter
        return (
          <KeywordFilter
            defaultFilter={filterToEdit?.filter}
            onChange={changeState("text")}
            column={
              {
                key: columnKey,
              } as unknown as TFilterColumnConfig
            }
          />
        )
      }

      if (isTextFilter(column.type)) {
        return (
          <KeywordFilter
            defaultFilter={filterToEdit?.filter}
            column={column}
            onChange={changeState("text")}
          />
        )
      } else if (isRangeFilter(column.type)) {
        return (
          <RangeFilter
            tableFilter={tableFilter}
            column={column}
            onChange={changeState("range")}
          />
        )
      } else if (isDateFilter(column.type)) {
        return (
          <DateFilter
            tableFilter={tableFilter}
            column={column}
            onChange={changeState("date")}
          />
        )
      } else if (isSelectFilter(column.type)) {
        return (
          <SelectFilter
            tableFilter={tableFilter}
            column={column}
            onChange={changeState("select")}
            getOptions={getSelectOptions}
          />
        )
      }
    },
    [columnConfig, tableFilter, getSelectOptions],
  )

  return (
    <div
      className={cn(
        "flex flex-col w-72 top-[120%] border right-[0%] border-basic-blue rounded-lg absolute z-[50] bg-basic-dark-blue px-4 pb-2 gap-4",
        {
          "right-[0%]": align === "right",
          "left-[0%]": align === "left",
        },
      )}
    >
      <div className="flex items-center justify-between">
        <p className="font-bold text-4">Filter column</p>
        <Button
          variant={{ variant: "text" }}
          onClick={handleResetFilters}
          className="text-basic-grey-inactive hover:text-white hover:opacity-100"
        >
          Reset
        </Button>
      </div>

      <section>
        <div className="space-y-3">
          <label className="ml-1 font-semibold">Column</label>
          <Select
            options={options}
            defaultValue={filterToEdit?.filter?.id ?? ""}
            onChange={handleSelect}
            className="bg-basic-blue text-white font-semibold"
          />
        </div>
        <Divider className="my-6 bg-basic-blue" />
        {selectedColumn && renderFilters(selectedColumn)}
        {selectedColumn && <Divider className="my-6 bg-basic-blue" />}
        <div className="text-center flex flex-col gap-2">
          <Button
            disabled={!selectedColumn}
            size="lg"
            className="font-bold"
            onClick={handleApplyFilters}
          >
            Apply
          </Button>
        </div>
      </section>
    </div>
  )
}

export default Menu
